/**
 * Rebuy "Choose Your Options" modal: Embla slider with thumbnails.
 *
 * The modal is rendered by Rebuy's own Vue app, so we never restructure its
 * markup. Instead we read the image URLs from Rebuy's sub-images, build our
 * own gallery inside .rebuy-modal__product-media and hide Rebuy's image and
 * sub-images with CSS. Quantity, options and Add To Bundle stay entirely
 * Rebuy's job. When Rebuy swaps its main image (variant change) the slider
 * follows; when the modal closes the Embla instances are destroyed.
 *
 * Loaded only on the Bundle & Save page (see quote-section.liquid).
 */

(function () {
  const MODAL = '.rebuy-modal';
  const MEDIA = '.rebuy-modal__product-media';
  const MAIN_IMAGE = '.rebuy-modal__product-media-image';
  const SUB_IMAGES = '.rebuy-modal__product-sub-image img';
  const GALLERY = 'jg-modal-gallery';
  const MAIN_SIZE = 800;
  const THUMB_SIZE = 200;

  const ARROW_PATH = {
    prev: 'M15 18l-6-6 6-6',
    next: 'M9 18l6-6-6-6'
  };

  const galleries = new Set();
  let frame = 0;

  /* Rebuy serves Shopify CDN images with a legacy _400x400 size suffix. */
  function resize(src, size) {
    return src.replace(/_\d+x\d*(?=\.[a-z0-9]+(?:\?|$))/i, '_' + size + 'x' + size);
  }

  /* Same image regardless of size suffix or query string. */
  function imageKey(src) {
    return resize(src || '', 0).split('?')[0];
  }

  function element(tag, className, attributes) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    Object.keys(attributes || {}).forEach(function (name) {
      node.setAttribute(name, attributes[name]);
    });
    return node;
  }

  function arrow(direction) {
    const button = element('button', GALLERY + '__arrow ' + GALLERY + '__arrow--' + direction, {
      type: 'button',
      'aria-label': direction === 'prev' ? 'Previous image' : 'Next image'
    });
    button.innerHTML =
      '<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="' + ARROW_PATH[direction] + '"/></svg>';
    return button;
  }

  function readImages(media) {
    const seen = {};
    const images = [];
    let nodes = media.querySelectorAll(SUB_IMAGES);
    if (!nodes.length) nodes = media.querySelectorAll(MAIN_IMAGE);

    nodes.forEach(function (img) {
      const src = img.getAttribute('src');
      if (!src || seen[imageKey(src)]) return;
      seen[imageKey(src)] = true;
      images.push({ src: src, alt: img.getAttribute('alt') || '' });
    });
    return images;
  }

  function build(media, images) {
    const root = element('div', GALLERY);
    const main = element('div', GALLERY + '__main');
    const viewport = element('div', GALLERY + '__viewport');
    const container = element('div', GALLERY + '__container');
    const thumbsViewport = element('div', GALLERY + '__thumbs');
    const thumbsContainer = element('div', GALLERY + '__thumbs-container');
    const thumbs = [];

    images.forEach(function (image, index) {
      const slide = element('div', GALLERY + '__slide');
      slide.appendChild(
        element('img', GALLERY + '__image', {
          src: resize(image.src, MAIN_SIZE),
          alt: image.alt,
          loading: index === 0 ? 'eager' : 'lazy'
        })
      );
      container.appendChild(slide);

      const thumb = element('button', GALLERY + '__thumb', {
        type: 'button',
        'aria-label': 'Show image ' + (index + 1) + ' of ' + images.length
      });
      thumb.appendChild(element('img', '', { src: resize(image.src, THUMB_SIZE), alt: '', loading: 'lazy' }));
      thumbsContainer.appendChild(thumb);
      thumbs.push(thumb);
    });

    viewport.appendChild(container);
    main.appendChild(viewport);
    thumbsViewport.appendChild(thumbsContainer);
    root.appendChild(main);

    const single = images.length < 2;
    let prev = null;
    let next = null;
    if (!single) {
      prev = arrow('prev');
      next = arrow('next');
      main.appendChild(prev);
      main.appendChild(next);
      root.appendChild(thumbsViewport);
    }

    media.appendChild(root);

    const mainApi = EmblaCarousel(viewport, { loop: false });
    const thumbsApi = single ? null : EmblaCarousel(thumbsViewport, { containScroll: 'keepSnaps', dragFree: true });

    function onSelect() {
      const selected = mainApi.selectedScrollSnap();
      thumbs.forEach(function (thumb, index) {
        const active = index === selected;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (thumbsApi) thumbsApi.scrollTo(selected);
      if (prev) prev.disabled = !mainApi.canScrollPrev();
      if (next) next.disabled = !mainApi.canScrollNext();
    }

    if (!single) {
      prev.addEventListener('click', function () {
        mainApi.scrollPrev();
      });
      next.addEventListener('click', function () {
        mainApi.scrollNext();
      });
      thumbs.forEach(function (thumb, index) {
        thumb.addEventListener('click', function () {
          mainApi.scrollTo(index);
        });
      });
    }

    mainApi.on('select', onSelect);
    mainApi.on('reInit', onSelect);
    onSelect();

    return {
      media: media,
      root: root,
      key: images.map(function (image) {
        return imageKey(image.src);
      }).join('|'),
      images: images,
      mainSrc: null,
      destroy: function () {
        mainApi.destroy();
        if (thumbsApi) thumbsApi.destroy();
        if (root.parentNode) root.parentNode.removeChild(root);
      },
      scrollTo: function (index) {
        mainApi.scrollTo(index);
      }
    };
  }

  /* Follow Rebuy's own main image when it changes, e.g. on a variant swap. */
  function followMainImage(gallery) {
    const mainImage = gallery.media.querySelector(MAIN_IMAGE);
    const src = mainImage ? mainImage.getAttribute('src') : null;
    if (!src || src === gallery.mainSrc) return;

    const first = gallery.mainSrc === null;
    gallery.mainSrc = src;
    if (first) return;

    const key = imageKey(src);
    for (let i = 0; i < gallery.images.length; i++) {
      if (imageKey(gallery.images[i].src) === key) {
        gallery.scrollTo(i);
        return;
      }
    }
  }

  function isVisible(node) {
    return node.getClientRects().length > 0;
  }

  function sync() {
    // Tear down galleries whose modal has closed or been re-rendered.
    galleries.forEach(function (gallery) {
      if (!gallery.media.isConnected || !gallery.root.isConnected) {
        gallery.destroy();
        delete gallery.media._jgGallery;
        galleries.delete(gallery);
      }
    });

    if (typeof EmblaCarousel === 'undefined') return;

    document.querySelectorAll(MODAL + ' ' + MEDIA).forEach(function (media) {
      // Embla measures slides on init, so wait until the modal is actually shown.
      if (!isVisible(media)) return;

      const images = readImages(media);
      if (!images.length) return;

      const key = images.map(function (image) {
        return imageKey(image.src);
      }).join('|');

      let gallery = media._jgGallery;
      if (gallery && gallery.key !== key) {
        // Same modal reused for a different product.
        gallery.destroy();
        galleries.delete(gallery);
        gallery = null;
      }

      if (!gallery) {
        gallery = media._jgGallery = build(media, images);
        galleries.add(gallery);
      }

      followMainImage(gallery);
    });
  }

  function schedule() {
    if (frame) return;
    frame = window.requestAnimationFrame(function () {
      frame = 0;
      sync();
    });
  }

  function touchesModal(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.closest('.' + GALLERY)) return false;
    return !!(node.closest(MODAL) || node.querySelector(MODAL) || node.matches(MODAL));
  }

  /* Rebuy opens, re-renders and closes the modal on its own, so watch for it. */
  new MutationObserver(function (records) {
    for (let i = 0; i < records.length; i++) {
      const record = records[i];
      const target = record.target.nodeType === 1 ? record.target : record.target.parentNode;

      if (touchesModal(target)) {
        schedule();
        return;
      }

      const changed = Array.prototype.slice
        .call(record.addedNodes)
        .concat(Array.prototype.slice.call(record.removedNodes));
      for (let j = 0; j < changed.length; j++) {
        if (touchesModal(changed[j])) {
          schedule();
          return;
        }
      }
    }
  }).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'src']
  });

  schedule();
})();
